/*
 * Tyrer-Cuzick (IBIS v8) method, re-implemented from the published description
 * (Tyrer, Duffy & Cuzick 2004; Brentnall & Cuzick, arXiv:1907.02829), with US baseline rates.
 * Its weights were first matched to one-factor runs of the official IBIS v8.0b program, then refitted
 * to 151 runs of a published IBIS v8 calculator that uses US rates.
 *
 * NOT the licensed IBIS software. Pieces IBIS never published are replaced with
 * stated approximations (marked APPROX below), so numbers will differ from IBIS.
 *
 * Steps (as in the specification):
 *   A  genetic segregation: 6 genotype states (BRCA1/BRCA2/none × unknown gene),
 *      pedigree likelihood, Bayes posterior, familial hazard h_G(t)
 *   B  personal factors: normalised relative hazards r(x2), HRT time profile, BMI rules
 *   C  density relative risk (age 40+)
 *   D  h1 = h_G × r × RR_density
 *   E  absolute risk with competing mortality; atypia/LCIS override
 */
(function (root, factory) {
  const api = factory();
  if (typeof module !== "undefined" && module.exports) module.exports = api;
  else root.RozTC = api;
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";
  const isNum = (v) => typeof v === "number" && isFinite(v);
  const MAXA = 121; // ages 0..120

  /* ---------- published parameters ---------- */
  // Published as allele frequencies (BRCA1 0.0006, BRCA2 0.0010, unknown gene 0.114); a woman
  // carries a dominant gene if either of her two copies has it.
  const PRIOR_BRCA1 = 0.0012, PRIOR_BRCA2 = 0.0020;          // carrier frequencies
  const BETA_ALLELE = 0.08451;                         // unknown dominant gene
  const BETA = 1 - Math.pow(1 - BETA_ALLELE, 2);     // share of women who carry it
  const GAMMA = 12.87;                               // its relative hazard
  const LIFETIME_AGE = 85;

  /* ---------- APPROX: BRCA penetrance (annual hazards by decade). IBIS's own tables are not
     published, so these start from Antoniou 2003 (breast) and Kuchenbaecker 2017 (ovarian) and are
     fitted to IBIS v8 results for carriers, carriers' daughters and families with young cancers. */
  const DEC = (arr) => (a) => (a < 20 ? 0 : arr[Math.min(arr.length - 1, Math.floor((a - 20) / 10))]);
  const BRCA1_BREAST = DEC([0.002416,0.01477,0.03041,0.02364,0.01548,0.02228,0.02016]);
  const BRCA2_BREAST = DEC([0.003076,0.01318,0.01803,0.01991,0.02924,0.02442,0.02777]);
  const OV_POP = DEC([0.00005, 0.00005, 0.0001, 0.0002, 0.0003, 0.00035, 0.00035]);
  const OV_BRCA1 = DEC([0.0003466,0.0003466,0.003466,0.006933,0.006933,0.00522,0.00522]);
  const OV_BRCA2 = DEC([0.001101,0.001101,0.001101,0.01101,0.01321,0.01321,0.01321]);
  // APPROX: male breast cancer points more strongly to BRCA (BRCA2 above all) than a woman's would
  const MALE_BRCA = [1,1.794,1.282];

  /* ---------- US baseline: annual rates by five-year age group from 20 (20–24 … 85+) ----------
     Competing mortality: US vital statistics 1985–87, as published in the NCI Breast Cancer Risk
     Assessment Tool. Breast cancer incidence: fitted so that the US average lifetime risk matches the
     US-rate IBIS v8 calculator at every age from 20 to 84 (to within 0.05 points). */
  const US_INCIDENCE = [0.00001119,0.000085,0.0002951,0.0007234,0.001391,0.002121,0.00254,0.003106,0.003703,0.003905,0.003575,0.003703,0.004023,0.004077];
  const US_MORTALITY = [0.000493, 0.000531, 0.000625, 0.000825, 0.001307, 0.002181, 0.003655, 0.005852, 0.009439, 0.015028, 0.023839, 0.038832, 0.066828, 0.144908];
  let cache = null;
  function tables() {
    if (cache) return cache;
    const L1 = US_INCIDENCE, L2 = US_MORTALITY;
    const grp = (a) => Math.min(13, Math.max(0, Math.floor((a - 20) / 5)));
    const hPop = [], hMort = [];
    for (let a = 0; a < MAXA; a++) { hPop.push(a < 20 ? 0 : L1[grp(a)]); hMort.push(a < 20 ? L2[0] : L2[grp(a)]); }
    const surv = (h) => { const S = [1]; for (let a = 0; a < MAXA; a++) S.push(S[a] * Math.exp(-h(a))); return S; };
    const Spop = surv((a) => hPop[a]);
    const SB = [null, surv(BRCA1_BREAST), surv(BRCA2_BREAST)];
    // non-carrier breast survival so that the population mix reproduces population incidence
    const Snc = Spop.map((s, a) => (s - PRIOR_BRCA1 * SB[1][a] - PRIOR_BRCA2 * SB[2][a]) / (1 - PRIOR_BRCA1 - PRIOR_BRCA2));
    const byC1 = [Snc, SB[1], SB[2]];
    // split each BRCA state by the unknown gene: (1-β)S0 + β S0^γ = S_c1 (Newton-Raphson)
    const S = byC1.map((Sc) => {
      const S0 = Sc.map((target) => {
        let x = Math.min(1, Math.max(1e-12, target));
        for (let i = 0; i < 60; i++) {
          const f = (1 - BETA) * x + BETA * Math.pow(x, GAMMA) - target;
          const d = (1 - BETA) + BETA * GAMMA * Math.pow(x, GAMMA - 1);
          const nx = Math.min(1, Math.max(1e-15, x - f / d));
          if (Math.abs(nx - x) < 1e-14) { x = nx; break; }
          x = nx;
        }
        return x;
      });
      return [S0, S0.map((x) => Math.pow(x, GAMMA))];
    });
    const So = [surv(OV_POP), surv(OV_BRCA1), surv(OV_BRCA2)];
    return (cache = { hPop, hMort, S, So });
  }

  /* ---------- A · genetic segregation ---------- */
  // States as [BRCA gene (0 none, 1 BRCA1, 2 BRCA2), unknown gene (0/1)]; index = c1 * 2 + c2.
  const STATES = [];
  for (let c1 = 0; c1 < 3; c1++) for (let c2 = 0; c2 < 2; c2++) STATES.push([c1, c2]);
  const cap = (a) => Math.max(0, Math.min(MAXA - 1, Math.round(a)));
  // Ashkenazi Jewish founder mutations (185delAG, 5382insC; 6174delT) raise the carrier priors.
  const ASHKENAZI_BRCA1 = 0.011, ASHKENAZI_BRCA2 = 0.013;

  /* Pedigree peeling. Each person carries three dominant loci (BRCA1, BRCA2, the unknown gene),
     coded as a bitmask m = b1 | b2 << 1 | u << 2. Founders follow Hardy-Weinberg at each locus and
     a child gets each allele from each parent with Mendelian probabilities, so relatives who share
     a parent are correlated through that parent instead of being counted independently.
     APPROX: aunts' and cousins' grandparents are peeled exactly, but each cousin is taken to come
     from a separate uncle, and nieces from a separate sibling. */
  const M8 = [0, 1, 2, 3, 4, 5, 6, 7];
  const idxOf = (m) => ((m & 1) ? 1 : (m & 2) ? 2 : 0) * 2 + ((m & 4) ? 1 : 0); // BRCA1 + BRCA2 together scores as BRCA1
  const kcache = {};
  function kernels(pB1, pB2) {
    const key = pB1 + ":" + pB2;
    if (kcache[key]) return kcache[key];
    const pis = [pB1, pB2, BETA];
    const q = pis.map((p) => 1 - Math.sqrt(1 - p));          // allele frequency
    const tc = pis.map((p, i) => q[i] / p);                   // chance a carrier passes it on
    const bit = (m, i) => (m >> i) & 1;
    const per = (fn) => M8.map((c) => [0, 1, 2].reduce((acc, i) => acc * (bit(c, i) ? fn(i) : 1 - fn(i)), 1));
    const f = per((i) => pis[i]);
    const t = M8.map((a) => M8.map((b) => per((i) => 1 - (1 - (bit(a, i) ? tc[i] : 0)) * (1 - (bit(b, i) ? tc[i] : 0)))));
    const r = M8.map((a) => per((i) => 1 - (1 - (bit(a, i) ? tc[i] : 0)) * (1 - q[i])));
    return (kcache[key] = { f, t, r });
  }
  function relLikelihood(T, rel, g) {
    const { S, So } = T;
    const Sg = S[g[0]][g[1]];
    let L = 1;
    if (rel.test === "brca1" && g[0] !== 1) return 0;
    if (rel.test === "brca2" && g[0] !== 2) return 0;
    if (rel.test === "neg" && g[0] !== 0) return 0;
    // APPROX: IBIS weighs a man's breast cancer about as heavily as a woman's at the same age,
    // so an affected male relative is scored with the female penetrance; unaffected men add nothing.
    if (rel.male && rel.breast !== "yes") return L;
    if (rel.breast === "yes") {
      const a = cap(rel.breastAge);
      L *= (Sg[a] - Sg[a + 1]) * (rel.male ? MALE_BRCA[g[0]] : 1);
      if (rel.bilateralAge != null) { const b = cap(rel.bilateralAge); L *= Math.max(1e-12, -Math.log(Sg[b + 1] / Sg[b])); }
    } else if (rel.breast === "no") L *= Sg[cap(Math.min(rel.age, UNAFFECTED_AGE))];
    if (rel.male) return L;
    if (rel.ovarian === "yes") { const a = cap(rel.ovarianAge); L *= So[g[0]][a] - So[g[0]][a + 1]; }
    else if (rel.ovarian === "no") L *= So[g[0]][cap(rel.age)];
    return L;
  }

  // APPROX: a relative free of breast cancer counts as free of it only up to this age. IBIS gives long
  // healthy lives in the family far less weight than a plain survival term would.
  const UNAFFECTED_AGE = 61;

  /* ---------- pedigree from the app's answers ---------- */
  const REL = { // default age offset from her (when not given), sex
    mother: [27], father: [30, true], sisters: [0], daughters: [-27], son: [-27, true], brothers: [0, true],
    nani: [54], dadi: [57], khalas: [27], phuppos: [30], halfSisM: [0], halfSisP: [0], nieces: [-25],
    chachaDaughters: [0], mamuDaughters: [0],
  };
  function pedigree(A, people) {
    const out = [];
    Object.keys(REL).forEach((g) => people(A, g).forEach((p) => {
      const [off, male] = REL[g];
      const age = isNum(p.age) ? p.age : Math.max(15, (A.age || 40) + off);
      const dflt = Math.max(25, Math.min(age, 55));
      const breastAge = p.breast === "yes" ? (isNum(p.breastAge) ? p.breastAge : dflt) : null;
      out.push({
        g, male: !!male, breast: p.breast, ovarian: p.ovarian, age,
        breastAge, bilateralAge: p.bilateral === "yes" ? (isNum(p.bilateralAge) ? p.bilateralAge : breastAge) : null,
        ovarianAge: p.ovarian === "yes" ? (isNum(p.ovarianAge) ? p.ovarianAge : dflt) : null,
        test: p.brca === "brca1" || p.brca === "brca2" || p.brca === "neg" ? p.brca : null,
      });
    }));
    return out;
  }

  function posterior(T, A, t0, rels) {
    const ash = A.ashkenazi === "yes";
    const K = kernels(ash ? ASHKENAZI_BRCA1 : PRIOR_BRCA1, ash ? ASHKENAZI_BRCA2 : PRIOR_BRCA2);
    const lik = (rel) => M8.map((m) => relLikelihood(T, rel, STATES[idxOf(m)]));
    const group = (g) => rels.filter((r) => r.g === g).map(lik);
    const sum8 = (fn) => M8.reduce((s, m) => s + fn(m), 0);
    const prodAll = (list, fn) => list.reduce((acc, Lv) => acc * fn(Lv), 1);
    const viaRandom = (p, Lv) => sum8((c) => K.r[p][c] * Lv[c]);   // child of p and someone unrelated
    // one side of the family, as a function of that parent's state: grandparents are founders
    function side(gm, gp, sibs, cousins) {
      const G = group(gm), S = group(sibs), C = group(cousins);
      return M8.map((par) => {
        let tot = 0;
        for (const a of M8) for (const b of M8) {
          const w = K.f[a] * K.f[b] * K.t[a][b][par];
          if (!w) continue;
          let x = w * prodAll(G, (Lv) => Lv[a]);
          x *= prodAll(S, (Lv) => sum8((s) => K.t[a][b][s] * Lv[s]));
          x *= prodAll(C, (Lv) => sum8((u) => K.t[a][b][u] * viaRandom(u, Lv)));
          tot += x;
        }
        return tot;
      });
    }
    const mat = side("nani", null, "khalas", "mamuDaughters"), pat = side("dadi", null, "phuppos", "chachaDaughters");
    const mum = group("mother"), dad = group("father"), sibs = group("sisters").concat(group("brothers"));
    const halfM = group("halfSisM"), halfP = group("halfSisP"), nieces = group("nieces"), kids = group("daughters").concat(group("son"));
    const self = M8.map((m) => {
      const [c1, c2] = STATES[idxOf(m)];
      if (A.brca === "brca1" && c1 !== 1) return 0;
      if (A.brca === "brca2" && c1 !== 2) return 0;
      if (A.brca === "neg" && c1 !== 0) return 0;
      let L = T.S[c1][c2][t0]; // she is free of breast cancer today
      if (A.ovarian === "yes") { const a = cap(isNum(A.ovarianAge) ? A.ovarianAge : t0); L *= T.So[c1][a] - T.So[c1][a + 1]; }
      return L * prodAll(kids, (Lv) => viaRandom(m, Lv));
    });
    const w = new Array(6).fill(0);
    for (const gm of M8) for (const gf of M8) {
      let x = mat[gm] * pat[gf] * prodAll(mum, (Lv) => Lv[gm]) * prodAll(dad, (Lv) => Lv[gf]);
      if (!x) continue;
      x *= prodAll(sibs, (Lv) => sum8((s) => K.t[gm][gf][s] * Lv[s]));
      x *= prodAll(halfM, (Lv) => viaRandom(gm, Lv)) * prodAll(halfP, (Lv) => viaRandom(gf, Lv));
      x *= prodAll(nieces, (Lv) => sum8((s) => K.t[gm][gf][s] * viaRandom(s, Lv)));
      for (const d of M8) w[idxOf(d)] += x * K.t[gm][gf][d] * self[d];
    }
    const tot = w.reduce((a, b) => a + b, 0) || 1;
    return w.map((x) => x / tot);
  }
  // familial hazard from the posterior mix of genotype survivals, conditional on being well at t0
  function familialHazard(T, post, t0) {
    const h = new Array(MAXA).fill(0);
    let Sprev = 1;
    for (let a = t0; a < MAXA - 1; a++) {
      let Snext = 0;
      STATES.forEach((d, i) => { const Sd = T.S[d[0]][d[1]]; if (post[i]) Snext += post[i] * Sd[a + 1] / Sd[t0]; });
      h[a] = Sprev > 0 && Snext > 0 ? -Math.log(Snext / Sprev) : h[a - 1] || 0;
      Sprev = Snext;
    }
    return h;
  }

  /* ---------- B · personal factors ----------
     Relative hazards matched to one-factor runs of IBIS v8 with US rates (competing mortality on),
     each against a neutral woman: menarche 13, first birth 25, 1.63 m, BMI 23.7, menopause at 50. */
  const interp = (pts, x) => {
    if (x <= pts[0][0]) return pts[0][1];
    for (let i = 1; i < pts.length; i++) if (x <= pts[i][0]) { const [x0, y0] = pts[i - 1], [x1, y1] = pts[i]; return y0 + (y1 - y0) * (x - x0) / (x1 - x0); }
    return pts[pts.length - 1][1];
  };
  const MENARCHE = {"10":1.166,"11":1.065,"12":1.065,"13":1,"14":0.9754,"15":0.9233,"16":0.8738,"17":0.8048}; // 10 or under … 17 or over
  function menarcheRR(m) { return isNum(m) ? MENARCHE[Math.min(17, Math.max(10, Math.round(m)))] : 1; }
  const FIRST_BIRTH = [[15,0.7123],[18,0.7946],[20,0.8017],[22,0.8866],[25,1],[28,1.111],[30,1.209],[32,1.247],[35,1.355]];
  const NULLIPAROUS = 1.23;
  function firstBirthRR(A, parity) {
    if (parity === "no") return NULLIPAROUS;
    if (parity !== "yes" || !isNum(A.firstBirth)) return 1;
    return interp(FIRST_BIRTH, A.firstBirth);
  }
  // smooth in age at menopause, above 50 as well as below
  const MENO_SLOPE = 0.02444;
  function menopauseRR(A) { return A.meno === "post" && isNum(A.menoAge) ? Math.exp(MENO_SLOPE * (A.menoAge - 50)) : 1; }
  // under 1.60 m, rising from 1.60 to 1.70 m, 1.70 m and over; against the reference
  const HEIGHT = {"short":0.9779,"from":1.05,"slope":2,"tall":1.228,"ref":1.1};
  function heightRR(h) { if (!h) return 1; const v = h < 1.6 ? HEIGHT.short : h < 1.7 ? HEIGHT.from + HEIGHT.slope * (h - 1.6) : HEIGHT.tall; return v / HEIGHT.ref; }
  // after menopause, by BMI band: under 21, 21–23, 23–25, 25–27, 27 and over; against the reference
  const BMI = {"bands":[0.9858,1.14,1.15,1.255,1.31],"ref":1.24};
  function bmiRR(A, bmi) {
    if (A.meno !== "post" || !bmi) return 1;
    return BMI.bands[bmi < 21 ? 0 : bmi < 23 ? 1 : bmi < 25 ? 2 : bmi < 27 ? 3 : 4] / BMI.ref;
  }
  // Where she is now in the menopause, by her age today. A neutral woman sits below the population
  // average (the average includes women with risk factors), by these factors.
  // APPROX: the premenopausal level rises with her age (a woman still premenopausal at 50 will reach
  // menopause later); perimenopausal and "don't know" are that level times a factor matched at age 48;
  // "don't know" before 45 counts as premenopausal.
  const LEVEL_PRE = [[25,0.9374],[35,0.9412],[45,0.9475],[50,1.012]];
  const LEVEL_POST = [[50,0.9074],[60,0.9278],[70,0.9069],[80,0.9313]];
  const LEVEL_PERI = 0.8362;
  const LEVEL_UNKNOWN = 0.8482;
  function statusLevel(A) {
    if (A.meno === "post") return interp(LEVEL_POST, A.age);
    const pre = interp(LEVEL_PRE, A.age);
    if (A.meno === "peri") return pre * LEVEL_PERI;
    if (A.meno === "unknown" && A.age >= 45) return pre * LEVEL_UNKNOWN;
    return pre;
  }
  // HRT: extra hazard while she keeps using it, fading after HRT.hold years over HRT.fade years.
  // The extra shrinks as BMI rises (heavier women already have more of their own oestrogen) and is
  // gone by BMI 30; for oestrogen-only HRT it then lowers risk slightly. Multiplier on the extra, by BMI.
  const HRT_BMI = {"combined":[[20,1.131],[24,1],[27,0.5593],[30,0.04522]],"oestrogen":[[20,1],[24,1],[30,-0.503]]};
  const HRT = {"combined":0.3532,"oestrogen":0.2279,"hold":8,"fade":10,"short":0.88};
  function hrtRR(A, bmi, t0, u) {
    if (A.hrt !== "current" && A.hrt !== "pastunder5") return 1;
    const top = A.hrtType === "combined" ? HRT.combined : HRT.oestrogen;
    const level = isNum(A.hrtYears) && A.hrtYears < 2 ? HRT.short : 1;
    let excess;
    if (A.hrt === "current") { const k = u - t0; excess = k < HRT.hold ? 1 : Math.max(0, 1 - (k - HRT.hold) / HRT.fade); }
    else { const since = (A.hrtAgo || 0) + (u - t0); excess = since < 1 ? 2 / 3 : since < 2 ? 1 / 3 : 0; }
    const byBmi = bmi ? interp(A.hrtType === "combined" ? HRT_BMI.combined : HRT_BMI.oestrogen, bmi) : 1;
    return Math.max(0.5, 1 + top * level * excess * byBmi);
  }
  const BENIGN = {"hyperplasia":1.985,"unknown":1.298};
  function benignRR(A) { return BENIGN[A.biopsy] || 1; }
  // Atypical hyperplasia and LCIS replace the family history and her other factors; only breast
  // density still counts. Their weight grows with age.
  const OVERRIDE = {"atypia":[[40,4.002],[60,4.349]],"lcis":[[40,7.76],[60,9.377]]};
  // BI-RADS density from age 40, matched at 40 and 60.
  const DENSITY = {"a":[[40,0.3885],[60,0.4743]],"b":[[40,0.5922],[60,0.7245]],"c":[[40,0.8918],[60,1.096]],"d":[[40,1.371],[60,1.695]]};
  const DENSITY_RR = { a: 0.4, b: 0.604, c: 0.907, d: 1.349 };
  const DENSITY_BMI = 0.025;

  /* ---------- E · absolute risk with competing mortality ---------- */
  function absRisk(T, h, rr, t0, t1) {
    let alive = 1, risk = 0;
    for (let u = t0; u < t1 && u < MAXA - 1; u++) {
      const h1 = h[u] * rr(u), h2 = T.hMort[u], tot = h1 + h2;
      if (tot > 0) risk += alive * (h1 / tot) * (1 - Math.exp(-tot));
      alive *= Math.exp(-tot);
    }
    return risk * 100;
  }

  /**
   * @param A answers; @param people (A, group) => relatives
   * @param extras {bmi, heightM, parity}
   */
  function estimate(A, people, extras) {
    if (!isNum(A.age) || A.age < 20 || A.age >= 90) return { ok: false, model: "tc", reason: "age", excluded: [] };
    const T = tables();
    const t0 = Math.round(A.age);
    const rels = A.famSkip ? [] : pedigree(A, people);
    const post = posterior(T, A, t0, rels);
    const postPop = posterior(T, { brca: null }, t0, []);
    const hG = familialHazard(T, post, t0);
    const hAvg = familialHazard(T, postPop, t0);

    const { bmi, heightM, parity } = extras;
    const rStatic = statusLevel(A) * menarcheRR(A.menarche) * firstBirthRR(A, parity) * menopauseRR(A) * heightRR(heightM) * bmiRR(A, bmi);
    // IBIS adjusts density for BMI: the same category means relatively denser breasts at a higher BMI.
    const dens = A.age >= 40 && DENSITY[A.density] ? interp(DENSITY[A.density], A.age) * (bmi ? Math.exp(DENSITY_BMI * (Math.min(bmi, 45) - 23.7)) : 1) : 1;
    const benign = benignRR(A);
    const overrideRR = A.lcis ? interp(OVERRIDE.lcis, A.age) : A.atypia ? interp(OVERRIDE.atypia, A.age) : 0;
    // combined risk: family × all other factors (atypia/LCIS kept out when they are the override)
    const rrAll = (u) => rStatic * dens * hrtRR(A, bmi, t0, u) * (overrideRR ? 1 : benign);
    // the override replaces family history and her other factors; density still counts
    const rrOverride = () => dens * overrideRR;
    const ten = Math.min(t0 + 10, 90);
    let hUse = hG, rrUse = rrAll, via = "combined";
    if (overrideRR && absRisk(T, hAvg, rrOverride, t0, ten) > absRisk(T, hG, rrAll, t0, ten)) { hUse = hAvg; rrUse = rrOverride; via = A.lcis ? "lcis" : "atypia"; }
    const one = () => 1;
    const pair = (t1) => ({ her: absRisk(T, hUse, rrUse, t0, t1), avg: absRisk(T, hAvg, one, t0, t1) });
    const curve = [];
    for (let a = t0 + 1; a <= Math.max(LIFETIME_AGE, ten); a++) { const p = pair(a); curve.push({ age: a, her: p.her, avg: p.avg }); }
    const fiveYear = pair(Math.min(t0 + 5, 90)), tenYear = pair(ten), lifetime = t0 < LIFETIME_AGE ? pair(LIFETIME_AGE) : null;
    const carrier = { brca1: (post[2] + post[3]) * 100, brca2: (post[4] + post[5]) * 100 };
    return {
      ok: true, model: "tc", via, fiveYear, tenYear, lifetime, curve,
      tenEndAge: ten, lifetimeAge: LIFETIME_AGE, populationLifetime: lifetime ? lifetime.avg : null,
      relativeRisk: tenYear.her / tenYear.avg, carrier, excluded: [],
      factors: { personal: rStatic, density: dens, benign: overrideRR ? 1 : benign, hrtNow: hrtRR(A, bmi, t0, t0) },
    };
  }

  return { estimate, tables, LIFETIME_AGE, PRIOR_BRCA1, PRIOR_BRCA2, BETA, BETA_ALLELE, GAMMA, DENSITY_RR, HRT, OVERRIDE };
});
